import type { ReactNode } from 'react'
import { BadgeCheck } from 'lucide-react'

import type { SkillSummary } from '@/hooks/use-profile'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { TopSkillLinkedEvidence } from '@/utils/top-skill-evidence-links'
import { formatTopSkillAriaMeta, formatTopSkillSubtitleLine } from '@/utils/top-skill-meta-display'

import { ProficiencyLabelBadge } from './proficiency-label-badge'

export function buildTopSkillAriaLabel(
  summary: SkillSummary,
  linkedEvidence?: TopSkillLinkedEvidence
): string {
  const { experiences: experienceCount, education: eduRows, certifications: certs } =
    summary.evidence_counts
  const proofTotal = experienceCount + eduRows + certs
  const metaLine = formatTopSkillAriaMeta(summary)

  const linkedParts: string[] = []
  for (const row of linkedEvidence?.experiences ?? []) {
    linkedParts.push(row.label)
  }
  for (const row of linkedEvidence?.educations ?? []) {
    linkedParts.push(row.label)
  }
  const linkedSuffix =
    linkedParts.length > 0 ? `. Linked to ${linkedParts.join('; ')}` : ''

  return `${summary.skill_name}, proved, ${metaLine}, ${proofTotal} proofs total (${experienceCount} ${experienceCount === 1 ? 'experience' : 'experiences'}, ${eduRows} education, ${certs} ${certs === 1 ? 'certification' : 'certifications'})${linkedSuffix}`
}

function TopSkillEvidenceList({
  heading,
  items,
}: {
  heading: string
  items: string[]
}) {
  if (items.length === 0) return null

  return (
    <div className="min-w-0 space-y-1">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{heading}</p>
      <ul className="min-w-0 space-y-0.5">
        {items.map((item, index) => (
          <li
            key={`${item}-${index}`}
            className="min-w-0 max-w-full truncate text-[11px] leading-snug text-foreground"
            title={item}
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function TopSkillTooltipContent({
  summary,
  linkedEvidence,
}: {
  summary: SkillSummary
  linkedEvidence?: TopSkillLinkedEvidence
}) {
  const { experiences: experienceCount, education: eduRows, certifications: certs } =
    summary.evidence_counts
  const experienceLabels = linkedEvidence?.experiences.map((row) => row.label) ?? []
  const educationLabels = linkedEvidence?.educations.map((row) => row.label) ?? []
  const showCertifications = certs > 0
  const subtitleLine = formatTopSkillSubtitleLine(summary)

  return (
    <div className="min-w-0 space-y-2 text-left">
      <div className="min-w-0 space-y-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <BadgeCheck className="size-3.5 shrink-0 text-success" aria-hidden strokeWidth={2.5} />
          <p
            className="min-w-0 truncate text-xs font-semibold leading-tight text-foreground"
            title={summary.skill_name}
          >
            {summary.skill_name}
          </p>
        </div>
        <ProficiencyLabelBadge label={summary.proficiency_label} className="text-[11px]" />
        {subtitleLine ? (
          <p
            className="truncate text-[11px] leading-tight text-muted-foreground"
            title={subtitleLine}
          >
            {subtitleLine}
          </p>
        ) : null}
      </div>

      <TopSkillEvidenceList heading="Experience" items={experienceLabels} />
      <TopSkillEvidenceList heading="Education" items={educationLabels} />

      {showCertifications ? (
        <p className="text-[11px] leading-tight text-muted-foreground">
          {certs} certification{certs === 1 ? '' : 's'}
        </p>
      ) : null}

      {experienceLabels.length === 0 &&
      educationLabels.length === 0 &&
      !showCertifications &&
      (experienceCount > 0 || eduRows > 0) ? (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] leading-tight text-muted-foreground">
          <span>Experience</span>
          <span className="text-right font-medium text-foreground">{experienceCount}</span>
          <span>Education</span>
          <span className="text-right font-medium text-foreground">{eduRows}</span>
        </div>
      ) : null}
    </div>
  )
}

export function TopSkillTooltip({
  summary,
  linkedEvidence,
  children,
}: {
  summary: SkillSummary
  linkedEvidence?: TopSkillLinkedEvidence
  children: ReactNode
}) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        sideOffset={6}
        hideArrow
        className={cn(
          'max-w-[min(100vw-2rem,280px)] rounded-lg border border-border bg-card px-3 py-2 text-foreground shadow-md',
        )}
      >
        <TopSkillTooltipContent summary={summary} linkedEvidence={linkedEvidence} />
      </TooltipContent>
    </Tooltip>
  )
}
